import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  iconCheckWhiteModalCourses,
  iconCloseModalCourses,
  iconFileModalCourses,
  iconFileSmallModalCourses,
} from '../../assets';
import { useTheme } from '../../theme';

const MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024; // 25MB
const ACCEPTED_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
];

function isAcceptedFile(file: File): boolean {
  const type = file.type?.toLowerCase() ?? '';
  const ext = file.name?.split('.').pop()?.toLowerCase() ?? '';
  if (ACCEPTED_TYPES.some(t => type === t)) return true;
  if (['pdf', 'docx', 'pptx', 'xlsx', 'xls'].includes(ext)) return true;
  return false;
}

export interface FileLessonModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (data: {
    files: File[];
    existingFiles: { id: string; name: string }[];
  }) => void;
  initialFiles?: File[];
  initialExistingFiles?: { id: string; name: string }[];
}

export function FileLessonModal({
  open,
  onClose,
  onConfirm,
  initialFiles = [],
  initialExistingFiles = [],
}: FileLessonModalProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [files, setFiles] = useState<File[]>(initialFiles);
  const [existingFiles, setExistingFiles] = useState<{ id: string; name: string }[]>(
    initialExistingFiles
  );
  const [dragOver, setDragOver] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFiles(initialFiles);
    setExistingFiles(initialExistingFiles);
  }, [open, initialFiles, initialExistingFiles]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose]
  );

  const handleConfirm = useCallback(() => {
    if (files.length === 0 && existingFiles.length === 0) return;
    onConfirm({
      files,
      existingFiles,
    });
    onClose();
  }, [files, existingFiles, onConfirm, onClose]);

  const appendNewFiles = useCallback((incoming: File[]) => {
    if (incoming.length === 0) return;
    setFiles(prev => {
      const key = (f: File) => `${f.name}::${f.size}::${f.lastModified}`;
      const existing = new Set(prev.map(key));
      const accepted = incoming.filter(file => {
        if (!isAcceptedFile(file)) return false;
        if (file.size > MAX_FILE_SIZE_BYTES) return false;
        const k = key(file);
        if (existing.has(k)) return false;
        existing.add(k);
        return true;
      });
      return accepted.length > 0 ? [...prev, ...accepted] : prev;
    });
  }, []);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const chosen = e.target.files ? Array.from(e.target.files) : [];
      appendNewFiles(chosen);
      e.target.value = '';
    },
    [appendNewFiles]
  );

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = e.dataTransfer.files ? Array.from(e.dataTransfer.files) : [];
    appendNewFiles(dropped);
  }, [appendNewFiles]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => setDragOver(false), []);

  if (!open) return null;

  const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    backgroundColor: theme.palette.createCourse.modalOverlay,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: theme.spacing.lg,
  };

  const modalStyle: React.CSSProperties = {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius.lg,
    boxShadow: theme.palette.createCourse.modalShadow,
    width: '100%',
    maxWidth: 480,
    maxHeight: '90vh',
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'column',
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing.lg,
    paddingBottom: 0,
    flexShrink: 0,
  };

  const headerLeftStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.sm,
  };

  const titleStyle: React.CSSProperties = {
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize.xl,
    fontWeight: theme.typography.fontWeight.bold,
    color: theme.palette.text.primary,
    margin: 0,
  };

  const closeButtonStyle: React.CSSProperties = {
    background: 'none',
    border: 'none',
    padding: theme.spacing.xs,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: theme.shape.borderRadius.sm,
    lineHeight: 1,
  };

  const bodyStyle: React.CSSProperties = {
    padding: theme.spacing.lg,
    flex: 1,
    minHeight: 0,
  };

  const subtitleStyle: React.CSSProperties = {
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize.base,
    fontWeight: theme.typography.fontWeight.semibold,
    color: theme.palette.text.primary,
    margin: 0,
    marginBottom: theme.spacing.sm,
  };

  const uploadZoneStyle: React.CSSProperties = {
    border: `2px dashed ${dragOver ? theme.palette.primary.main : theme.palette.border.main}`,
    borderRadius: theme.shape.borderRadius.sm,
    padding: theme.spacing.xl,
    textAlign: 'center',
    backgroundColor: dragOver
      ? theme.palette.createCourse.basicInfo.uploadZoneDragBg
      : theme.palette.background.default,
    cursor: 'pointer',
    transition: 'border-color 0.15s, background-color 0.15s',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing.sm,
    minHeight: 180,
  };

  const uploadHintStyle: React.CSSProperties = {
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize.sm,
    color: theme.palette.text.primary,
    margin: 0,
  };

  const uploadSpecStyle: React.CSSProperties = {
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize.xs,
    color: theme.palette.text.secondary,
    margin: 0,
  };

  const footerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: theme.spacing.sm,
    padding: theme.spacing.lg,
    paddingTop: 0,
    flexShrink: 0,
  };

  const cancelButtonStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize.base,
    fontWeight: theme.typography.fontWeight.semibold,
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.border.main}`,
    borderRadius: theme.shape.borderRadius.sm,
    cursor: 'pointer',
  };

  const confirmButtonStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing.sm,
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize.base,
    fontWeight: theme.typography.fontWeight.semibold,
    color: theme.palette.primary.contrastText,
    backgroundColor: theme.palette.primary.main,
    border: 'none',
    borderRadius: theme.shape.borderRadius.sm,
    cursor: 'pointer',
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="file-lesson-modal-title"
      style={overlayStyle}
      onKeyDown={handleKeyDown}
    >
      <div style={modalStyle} onClick={e => e.stopPropagation()}>
        <header style={headerStyle}>
          <div style={headerLeftStyle}>
            <img
              src={iconFileSmallModalCourses}
              alt=""
              width={24}
              height={24}
              style={{
                objectFit: 'contain',
                flexShrink: 0,
                filter: 'brightness(0)',
              }}
              aria-hidden
            />
            <h2 id="file-lesson-modal-title" style={titleStyle}>
              {t('courses.create.configure_file_lesson')}
            </h2>
          </div>
          <button
            type="button"
            style={closeButtonStyle}
            onClick={onClose}
            aria-label={t('courses.create.cancel')}
          >
            <img
              src={iconCloseModalCourses}
              alt=""
              width={20}
              height={20}
              style={{ objectFit: 'contain' }}
              aria-hidden
            />
          </button>
        </header>

        <div style={bodyStyle}>
          <p style={subtitleStyle}>
            {t('courses.create.upload_document_or_file')}
          </p>
          <div
            style={uploadZoneStyle}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() =>
              document.getElementById('file-lesson-upload-input')?.click()
            }
          >
            <input
              id="file-lesson-upload-input"
              type="file"
              multiple
              accept=".pdf,.docx,.pptx,.xls,.xlsx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            <img
              src={iconFileModalCourses}
              alt=""
              width={48}
              height={48}
              style={{
                objectFit: 'contain',
                filter: 'brightness(0) invert(0.5)',
              }}
              aria-hidden
            />
            <p style={uploadHintStyle}>
              {t('courses.create.click_to_upload_or_drag_drop')}
            </p>
            <p style={uploadSpecStyle}>
              {t('courses.create.file_formats_and_size')}
            </p>
            {(existingFiles.length > 0 || files.length > 0) && (
              <div
                style={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: theme.spacing.xs,
                  marginTop: theme.spacing.xs,
                }}
              >
                {existingFiles.map(existing => (
                  <div
                    key={`existing-${existing.id}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: theme.spacing.xs,
                      width: '100%',
                    }}
                  >
                    <span
                      style={{
                        fontSize: theme.typography.fontSize.xs,
                        color: theme.palette.primary.main,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        textAlign: 'left',
                        flex: 1,
                      }}
                    >
                      {existing.name}
                    </span>
                    <button
                      type="button"
                      onClick={e => {
                        e.stopPropagation();
                        setExistingFiles(prev => prev.filter(f => f.id !== existing.id));
                      }}
                      style={{
                        border: 'none',
                        background: 'transparent',
                        color: theme.palette.error.main,
                        cursor: 'pointer',
                        fontSize: theme.typography.fontSize.xs,
                      }}
                      aria-label={t('courses.create.delete_lesson_aria')}
                    >
                      ✕
                    </button>
                  </div>
                ))}
                {files.map((selected, idx) => (
                  <div
                    key={`new-${selected.name}-${selected.size}-${selected.lastModified}-${idx}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: theme.spacing.xs,
                      width: '100%',
                    }}
                  >
                    <span
                      style={{
                        fontSize: theme.typography.fontSize.xs,
                        color: theme.palette.primary.main,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        textAlign: 'left',
                        flex: 1,
                      }}
                    >
                      {selected.name}
                    </span>
                    <button
                      type="button"
                      onClick={e => {
                        e.stopPropagation();
                        setFiles(prev => prev.filter((_, i) => i !== idx));
                      }}
                      style={{
                        border: 'none',
                        background: 'transparent',
                        color: theme.palette.error.main,
                        cursor: 'pointer',
                        fontSize: theme.typography.fontSize.xs,
                      }}
                      aria-label={t('courses.create.delete_lesson_aria')}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <footer style={footerStyle}>
          <button
            type="button"
            style={cancelButtonStyle}
            onClick={onClose}
          >
            {t('courses.create.cancel')}
          </button>
          <button
            type="button"
            style={confirmButtonStyle}
            onClick={handleConfirm}
            disabled={files.length === 0 && existingFiles.length === 0}
          >
            <img
              src={iconCheckWhiteModalCourses}
              alt=""
              width={18}
              height={18}
              style={{ objectFit: 'contain', flexShrink: 0 }}
              aria-hidden
            />
            {t('courses.create.add_lesson')}
          </button>
        </footer>
      </div>
    </div>
  );
}
